import { createRoute, useNavigate } from '@tanstack/react-router';
import { Authenticate, layoutRoute, LoaderCtx, loaderFn } from '@/router/root-routes.tsx';
import { useSearchParams } from '@/hooks/use-search-params.ts';
import { getProductQuery } from '@/app/products/api/queries.ts';
import { getImgOrNoPreview, TProduct, TProductColor } from '@/types/models.ts';
import { useEffect, useState } from 'react';
import { TLabelValue } from '@/types/misc.ts';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import { usePageProps } from '@/hooks/use-page-props.ts';
import { Button } from '@/components/ui/button.tsx';
import { AppHeartIcon } from '@/app/products/app-heart-icon.tsx';
import { AddToProjectDrawer } from '@/app/products/add-to-project/add-to-project-drawer.tsx';
import { setAddToProjectItem, useUiStore } from '@/stores/ui.store.ts';
import { useAuthStore } from '@/stores/auth.store.ts';
import { getAdminProductUrl } from '@/app/products/api/endpoints.ts';
import { RequestCutting } from '@/app/products/view/request-cutting.tsx';
import { config } from '@/config';
import { AppAssistanceButton } from '@/components/app/app-assistance-button.tsx';

export const productViewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'products/$productId',
  component: ProductViewPage,
  beforeLoad: Authenticate(),
  loader: loaderFn(async (ctx: LoaderCtx) => {
    return await ctx.context.queryClient
      .ensureQueryData(getProductQuery(ctx.params.productId, ctx.location.search?.color_id))
      .catch((err: any) => {
        console.error('Error loading product data:', err);
        throw err;
      });
  }),
});

function getInitCurrentColor(product: TProduct, colorIdParam?: string) {
  if (colorIdParam) {
    const colorId = parseInt(colorIdParam, 10);
    const foundColor = product.colors.find((c) => c.id === colorId);
    if (foundColor) {
      return foundColor;
    }
  }
  return product.colors[0];
}

function getSpecs(product: TProduct): TLabelValue[] {
  const specs: TLabelValue[] = [
    { label: 'ID', value: product.id },
    { label: 'Type', value: product.type },
    { label: 'Look And Feel', value: product.looks },
    { label: 'Country', value: product.country },
    { label: 'Construction', value: product.construction },
    { label: 'Material', value: product.materials },
    { label: product.size_label || 'Size', value: product.size_str },
    { label: 'Patterns', value: product.patterns },
    { label: 'Pattern Repeat', value: product.pattern_repeat },
  ];
  return specs.filter((s) => s.value && String(s.value).length > 0);
}

function getMainImg(product: TProduct, color?: TProductColor) {
  if (color && color.img_full) {
    return getImgOrNoPreview(color.img_full);
  }
  return getImgOrNoPreview(product.img_full);
}

function prefetchImages(product: TProduct) {
  const imgUrls = product.colors.map((c) => c.img_full).filter((url) => url && url.length > 0);
  imgUrls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
}

function getMainImgAlt(product: TProduct, color?: TProductColor) {
  if (color) {
    return `${product.name} - ${color.name}`;
  }
  return product.name;
}

function getProductName(product: TProduct, color?: TProductColor) {
  return color ? `${product.name} - ${color.name}` : product.name;
}

function getAssistanceMessage(product: TProduct, color?: TProductColor) {
  return `I am looking for help with product ${getProductName(product, color)}, ID: ${product.id}.`;
}

type LightboxSlide = {
  src: string;
  title: string;
};
type LightboxState = {
  index: number;
  slides: LightboxSlide[];
};

export function ProductViewPage() {
  const { params } = useSearchParams();
  const product = productViewRoute.useLoaderData() as TProduct;
  const [color, setColor] = useState(getInitCurrentColor(product, params.color_id));
  const [lightboxState, setLightboxState] = useState<LightboxState | null>(null);
  const addToProjectItem = useUiStore((state) => state.addToProjectItem);
  const navigate = useNavigate();
  const user = useAuthStore((state) => state.user);

  const [showRequestCutting, setShowRequestCutting] = useState(false);

  useEffect(() => {
    prefetchImages(product);
  }, []);
  usePageProps({
    title: getMainImgAlt(product, color),
  });
  const openLightbox = () => {
    const slides: LightboxSlide[] = [];
    let index = 0;
    product.colors?.map((c, i) => {
      if (c.id === color?.id) {
        index = i;
      }
      slides.push({
        src: c.img_full,
        title: `${product.name} - ${c.name}`,
      });
    });
    setLightboxState({ index, slides });
  };
  const onColorChange = (c: TProductColor) => {
    setColor(c);
    // noinspection JSIgnoredPromiseFromCall
    navigate({
      search: (prev: any) => ({
        ...prev,
        color_id: c.id,
      }),
      replace: true,
    } as any);
  };

  const onAddToProject = () => {
    setAddToProjectItem({
      productId: product.id,
      colorId: color ? color.id : 0,
      name: product.name + (color ? ` - ${color.name}` : ''),
    });
  };

  const onSpecSheet = () => {
    const url = config.getApiUrl(`products/${product.id}/spec-sheet`);
    window.open(url, '_blank');
  };

  return (
    <div className="max-w-8xl mx-auto mt-4 xl:mt-8" data-color-id={params.color_id}>
      <div className="grid md:grid-cols-2 gap-6 xl:gap-20">
        <div className="cursor-pointer flex items-center justify-center" onClick={() => openLightbox()}>
          <img src={getMainImg(product, color)} alt={getMainImgAlt(product, color)} className="object-cover" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold">{product.name}</h1>
          {color && <h2>{color.name}</h2>}
          <div className="mt-4 flex flex-col gap-2">
            {getSpecs(product).map((spec) => (
              <div key={spec.label} className="flex gap-2 text-lg">
                <span className="font-medium xl: min-w-32">{spec.label}:</span>
                <span dangerouslySetInnerHTML={{ __html: spec.value }} />
              </div>
            ))}
          </div>
          <hr className="my-4" />
          <h3>Available Colors</h3>
          <ScrollArea viewportClassName="max-h-92" type="always">
            <div className="flex flex-wrap gap-4 mt-2">
              {product.colors.map((c) => (
                <div key={c.id} className="group cursor-pointer" onClick={() => onColorChange(c)}>
                  <div className="border-2 p-1 rounded group-hover:border-blue-500">
                    <div className="w-32 h-32 overflow-hidden flex">
                      <img
                        src={getImgOrNoPreview(c.img_thumb)}
                        alt={c.name}
                        className="object-cover rounded min-w-full"
                      />
                    </div>
                  </div>
                  <div className="text-center mt-1">{c.name}</div>
                </div>
              ))}
            </div>
          </ScrollArea>
          <div className="uppercase mt-6 text-lg flex gap-3">
            <Button variant="outline" size="lg" className="font-semibold" onClick={onAddToProject}>
              <AppHeartIcon filled={product.in_projects} />
              ADD TO PROJECT
            </Button>
            {product.type === 'Broadloom' && (
              <Button variant="outline" size="lg" onClick={() => setShowRequestCutting(true)}>
                REQUEST A CUTTING
              </Button>
            )}
            <Button variant="outline" size="lg" onClick={onSpecSheet}>
              SPEC-SHEET
            </Button>
            <AppAssistanceButton
              title={getProductName(product, color)}
              message={getAssistanceMessage(product, color)}
              subject={`Product assistance request from ${user?.full_name}`}
            />
          </div>
          {user?.admin && (
            <div className="mt-4">
              <Button variant="outline" size="lg" asChild>
                <a href={getAdminProductUrl(product.id, color?.id)} target="_blank">
                  BACKEND
                </a>
              </Button>
            </div>
          )}
        </div>
      </div>
      <Lightbox
        open={lightboxState !== null}
        plugins={[Captions]}
        close={() => setLightboxState(null)}
        slides={lightboxState?.slides}
        index={lightboxState?.index}
      />
      {addToProjectItem && <AddToProjectDrawer />}
      {showRequestCutting && (
        <RequestCutting product={product} color={color} onClose={() => setShowRequestCutting(false)} />
      )}
    </div>
  );
}
